import React from 'react';
import useLogPage from '../useLogPage';
import LogFilters from '../LogFilters';
import LogTable from '../LogTable';
import LogStatCards from '../LogStatCards';
import { TrendChart, DonutChart } from '../LogCharts';
import { LogService } from '../../../services/activityLogService';

const STATUS_OPTIONS = [
  { value: 'success', label: 'Success' },
  { value: 'failed',  label: 'Failed'  },
  { value: 'blocked', label: 'Blocked' },
];

const statusBadge = (v) => {
  const map = {
    success: ['#D1FAE5','#065F46'],
    failed:  ['#FEE2E2','#991B1B'],
    blocked: ['#FEF3C7','#92400E'],
  };
  const [bg, color] = map[v?.toLowerCase()] || ['#F3F4F6','#374151'];
  return <span className="al-badge" style={{ background: bg, color }}>{v || '—'}</span>;
};

const COLUMNS = [
  { key: 'id',         label: '#',          render: (v) => <span className="al-mono">{v}</span> },
  { key: 'user_id',    label: 'User ID',    render: (v) => <span className="al-mono">{v || '—'}</span> },
  { key: 'email',      label: 'Email' },
  { key: 'action',     label: 'Action' },
  { key: 'status',     label: 'Status',     render: statusBadge },
  { key: 'ip_address', label: 'IP Address', render: (v) => <span className="al-mono">{v || '—'}</span> },
  { key: 'device',     label: 'Device' },
  { key: 'created_at', label: 'Time',       render: (v) => v ? new Date(v).toLocaleString('en-IN') : '—' },
];

const AuthenticationLog = () => {
  const lp = useLogPage(LogService.authentication);

  const handleExport = () => {
    const { search, status, dateFrom, dateTo } = lp.filters;
    LogService.exportAuthentication({
      ...(search   ? { search }              : {}),
      ...(status   ? { status }              : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo   ? { date_to:   dateTo   } : {}),
      format: 'excel',
    });
  };

  // Use stats from API response; fall back to 0
  const s = lp.stats || {};
  const stats = [
    { label: 'Total Events', value: lp.totalItems.toLocaleString(), color: '#3B82F6' },
    { label: 'Success',      value: (s.success ?? 0).toLocaleString(),  color: '#10B981' },
    { label: 'Failed',       value: (s.failed  ?? 0).toLocaleString(),  color: '#EF4444' },
    { label: 'Blocked',      value: (s.blocked ?? 0).toLocaleString(),  color: '#F59E0B' },
  ];

  // Donut: prefer API donut array, else build from stats.breakdown
  const donutData = lp.donut.length
    ? lp.donut
    : Object.entries(s.breakdown || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="al-page">
      <LogStatCards stats={stats} />

      <div className="al-charts">
        <TrendChart title="Auth Events Over Time" data={lp.trend} color="#3B82F6" />
        <DonutChart  title="Status Breakdown"     data={donutData} />
      </div>

      <LogFilters
        filters={lp.filters}
        onFilterChange={lp.handleFilterChange}
        onApply={lp.handleApply}
        onReset={lp.handleReset}
        statusOptions={STATUS_OPTIONS}
        loading={lp.loading}
      />

      <LogTable
        title="Authentication Logs"
        columns={COLUMNS}
        rows={lp.rows}
        loading={lp.loading}
        error={lp.error}
        page={lp.page}
        totalPages={lp.totalPages}
        totalItems={lp.totalItems}
        pageSize={lp.filters.pageSize}
        onPageChange={lp.handlePageChange}
        onRefresh={lp.handleRefresh}
        onExport={handleExport}
      />
    </div>
  );
};

export default AuthenticationLog;
